<template>
  <div id="stoplight-container"></div>
</template>

<script>
import { mainAxios } from '@/mainAxios';
import '@stoplight/elements/styles.min.css';

export default {
  data() {
    return {
      groupName: this.$route.query.group || '',
      originalFetch: null,
      token: null,
      tokenEditado: null,
      authInput: null,
      mutationObserver: null,
      isLoaded: false
    }
  },
  computed: {
    specUrl() {
      const baseURL = mainAxios.defaults.baseURL;
      const cleanBase = baseURL.endsWith('/') ? baseURL : baseURL + '/';
      return cleanBase + "api/docs/" + this.groupName;
    }
  },
  mounted() {
    if (!this.groupName || this.groupName === 'null') {
      this.$toast.error("No se ha encontrado ninguna documentación, contacta con un administrador si esto no deberia estar ocurriendo.", {
        position: "top-right",
        timeout: 1500,
        onClose: () => this.$router.go(-1)
      });
      return;
    }

    this.token = localStorage.getItem('agenteAccessToken');
    this.tokenEditado = this.token;
    if (!this.token) {
      this.$toast.error("Es necesario iniciar sesión para acceder a este módulo.", {
        position: "top-right",
        timeout: 1500,
        onClose: () => this.$router.push({ name: 'Login' })
      });
      return;
    }
    if (!this.originalFetch) {
      this.originalFetch = window.fetch.bind(window);
    }

    window.fetch = this.fetchWithAuth.bind(this);
    this.loadStoplightElements();
  },

  beforeDestroy() {
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  },

  methods: {
    fetchWithAuth(resource, config = {}) {
      if (!config.headers) {
        config.headers = {};
      }
      const allInputs = document.querySelectorAll('input[type="text"]');
      let tokenAUsar = this.tokenEditado || this.token;
      for (let input of allInputs) {
        if (input.value && input.value.includes('Bearer')) {
          tokenAUsar = input.value;
          break;
        }
      }

      if (!tokenAUsar.startsWith('Bearer ')) {
        tokenAUsar = `Bearer ${tokenAUsar}`;
      }
      config.headers['Authorization'] = tokenAUsar;
      return this.originalFetch(resource, config);
    },

    loadStoplightElements() {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@stoplight/elements@7.16.6/web-components.min.js';
      script.onload = () => {
        this.initializeStoplight();
      };

      script.onerror = () => {
        this.$toast.error("Error al cargar Stoplight Elements", {
          onClose: () => this.$router.push({ name: 'Login' })
        });
      };
      document.head.appendChild(script);
    },
    async initializeStoplight() {
      try {
        const r = await fetch(this.specUrl);

        if (!r.ok) {
          const msg = await r.text();
          throw new Error(msg);
        }

        const spec = await r.json();

        this.addAuthorizationToSpec(spec);

        const sortedPaths = {};
        Object.keys(spec.paths)
          .sort((pathA, pathB) => {
            const methodA = Object.values(spec.paths[pathA])[0];
            const methodB = Object.values(spec.paths[pathB])[0];
            const summaryA = methodA?.summary || '';
            const summaryB = methodB?.summary || '';
            return summaryA.localeCompare(summaryB);
          })
          .forEach(path => {
            sortedPaths[path] = spec.paths[path];
          });

        spec.paths = sortedPaths;

        const container = document.getElementById('stoplight-container');
        if (!container) return;

        const elements = document.createElement('elements-api');
        const blob = new Blob([JSON.stringify(spec)], { type: 'application/json' });
        const specUrl = URL.createObjectURL(blob);

        elements.setAttribute('apiDescriptionUrl', specUrl);
        elements.setAttribute('router', 'hash');
        elements.setAttribute('layout', 'sidebar');

        container.appendChild(elements);
        this.isLoaded = true;
        this.waitForTokenInput();

      } catch (err) {
        this.$toast.error(`Error cargando documentación: ${err.message}`, {
          onClose: () => this.$router.push({ name: 'Login' })
        });
      }
    },
    waitForTokenInput() {
      const container = document.getElementById('stoplight-container');
      if (!container) return;
      this.mutationObserver = new MutationObserver(() => {
        this.attachListenersToAllAuthInputs();
      });
      this.mutationObserver.observe(container, {
        childList: true,
        subtree: true,
        characterData: false,
        attributes: false
      });
      this.attachListenersToAllAuthInputs();
    },
    attachListenersToAllAuthInputs() {
      const allInputs = document.querySelectorAll('input[type="text"]');
      allInputs.forEach(input => {
        if (input._hasAuthListener) return;
        if (input.value && input.value.includes('Bearer')) {
          this.addInputListeners(input);
        }
      });
    },
    addInputListeners(input) {
      if (!input || input._hasAuthListener) return;

      input._hasAuthListener = true;

      input.addEventListener('change', (e) => {
        this.tokenEditado = e.target.value;
        this.updateCurl(e.target.value);
      });

      input.addEventListener('input', (e) => {
        this.tokenEditado = e.target.value;
      });
    },

    addAuthorizationToSpec(spec) {
      const tokenWithBearer = this.token.startsWith('Bearer ')
        ? this.token
        : `Bearer ${this.token}`;
      Object.keys(spec.paths).forEach(path => {
        Object.keys(spec.paths[path]).forEach(method => {
          const operation = spec.paths[path][method];

          if (operation && typeof operation === 'object' && ['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
            if (!operation.parameters) {
              operation.parameters = [];
            }

            const authIndex = operation.parameters.findIndex(p => p.name === 'Authorization');
            if (authIndex >= 0) {
              operation.parameters[authIndex] = {
                name: 'Authorization',
                in: 'header',
                required: true,
                type: 'string',
                description: 'Bearer token (editable)',
                default: tokenWithBearer,
                example: tokenWithBearer
              };
            } else {
              operation.parameters.push({
                name: 'Authorization',
                in: 'header',
                required: true,
                type: 'string',
                description: 'Bearer token (editable)',
                default: tokenWithBearer,
                example: tokenWithBearer
              });
            }
          }
        });
      });
    },
    updateCurl(nuevoToken) {
      const tokenWithBearer = nuevoToken.startsWith('Bearer ')
        ? nuevoToken
        : `Bearer ${nuevoToken}`;
      const codeBlocks = document.querySelectorAll('code, pre');
      codeBlocks.forEach(block => {
        if (block.textContent.includes('Authorization:')) {
          block.textContent = block.textContent.replace(
            /Authorization: [^ ]*/g,
            `Authorization: ${tokenWithBearer}`
          );
        }
      });
    }
  }
}
</script>

<style scoped>
#stoplight-container ::v-deep * {
  max-width: unset !important;
}
</style>